import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Hub } from "aws-amplify/utils";
import { signOut } from "aws-amplify/auth";
import {
  ACCOUNT_CONTEXT_INVALID_EVENT,
  clearSelectedAccountId,
} from "../../shared/lib/auth/accountContext";
import {
  clearSessionProfileState,
  clearLogoutSessionCache,
  clearSignInSessionCache,
  markSessionAuthenticated,
  markSessionUnauthenticated,
} from "../auth/authSessionState";
import { getAuthSessionUser } from "../auth/getAuthSessionUser";
import { getLastKnownAuth } from "../auth/authStorage";
import { resetAccountScopedStores } from "../auth/sessionStoreReset";

const AuthContext = createContext(null);

function normalizeSessionIdentity(value) {
  return String(value || "").trim().toLowerCase();
}

function isSameSessionUser(currentUser, nextUser) {
  if (!currentUser || !nextUser) return false;

  const currentId = normalizeSessionIdentity(currentUser.id);
  const nextId = normalizeSessionIdentity(nextUser.id);
  if (currentId && nextId && currentId !== nextId) return false;

  const currentEmail = normalizeSessionIdentity(currentUser.email);
  const nextEmail = normalizeSessionIdentity(nextUser.email);
  if (currentEmail && nextEmail && currentEmail !== nextEmail) return false;

  return true;
}

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(() =>
    getLastKnownAuth()
  );

  const [status, setStatus] = useState("booting");
  const [user, setUser] = useState(null);
  const sessionGenerationRef = useRef(0);
  const userRef = useRef(null);

  const setSessionUser = useCallback((nextUser) => {
    userRef.current = nextUser;
    setUser(nextUser);
  }, []);

  const clearAuthenticatedSession = useCallback(() => {
    sessionGenerationRef.current += 1;
    setIsAuthenticated(false);
    setSessionUser(null);
    markSessionUnauthenticated({ clearPlan: true });
    setStatus("ready");
  }, [setSessionUser]);

  useEffect(() => {
    let isMounted = true;
    const sessionGeneration = sessionGenerationRef.current;

    async function validateSession() {
      try {
        const builtUser = await getAuthSessionUser({ forceRefresh: false });

        if (!isMounted || sessionGeneration !== sessionGenerationRef.current) return;

        if (builtUser) {
          setIsAuthenticated(true);
          setSessionUser(builtUser);
          markSessionAuthenticated(builtUser);
        } else {
          clearAuthenticatedSession();
        }
      } catch {
        if (!isMounted || sessionGeneration !== sessionGenerationRef.current) return;

        clearAuthenticatedSession();
      } finally {
        if (isMounted) {
          setStatus("ready");
        }
      }
    }

    validateSession();

    const handleAccountContextInvalid = () => {
      setUser((currentUser) => {
        if (!currentUser) return currentUser;

        clearSelectedAccountId(currentUser.email);
        resetAccountScopedStores();

        const nextUser = {
          ...currentUser,
          accountId: null,
          accountSelectionRequired: true,
        };

        userRef.current = nextUser;
        markSessionAuthenticated(nextUser);
        return nextUser;
      });
    };

    window.addEventListener(
      ACCOUNT_CONTEXT_INVALID_EVENT,
      handleAccountContextInvalid
    );

    const unsubscribe = Hub.listen("auth", async ({ payload }) => {
      const event = payload?.event;

      if (event === "signedIn") {
        try {
          clearSignInSessionCache();
          const sessionGeneration = sessionGenerationRef.current;
          const builtUser = await getAuthSessionUser();
          if (sessionGeneration !== sessionGenerationRef.current) return;

          setIsAuthenticated(true);
          setSessionUser(builtUser);
          markSessionAuthenticated(builtUser);
        } catch {
          setSessionUser(null);
          clearSessionProfileState();
        }
        setStatus("ready");
      }

      if (event === "signedOut") {
        clearAuthenticatedSession();
      }
    });

    return () => {
      isMounted = false;
      window.removeEventListener(
        ACCOUNT_CONTEXT_INVALID_EVENT,
        handleAccountContextInvalid
      );
      unsubscribe();
    };
  }, [clearAuthenticatedSession, setSessionUser]);

  const logout = useCallback(async () => {
    clearLogoutSessionCache();
    clearAuthenticatedSession();

    try {
      await signOut();
      clearLogoutSessionCache();
      clearAuthenticatedSession();
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
      throw error;
    }
  }, [clearAuthenticatedSession]);

  const refreshUser = useCallback(async (forceRefresh = true) => {
    const sessionGeneration = sessionGenerationRef.current;

    try {
      const builtUser = await getAuthSessionUser({ forceRefresh });

      if (sessionGeneration !== sessionGenerationRef.current) {
        return null;
      }

      if (builtUser) {
        setIsAuthenticated(true);
        setSessionUser(builtUser);
        markSessionAuthenticated(builtUser);
      } else {
        clearAuthenticatedSession();
      }

      return builtUser;
    } catch (error) {
      console.error("Erro ao atualizar sessão do usuário:", error);
      return null;
    }
  }, [clearAuthenticatedSession, setSessionUser]);

  const updateAuthenticatedUser = useCallback((nextUser) => {
    if (!isSameSessionUser(userRef.current, nextUser)) return null;

    setIsAuthenticated(true);
    setSessionUser(nextUser);
    markSessionAuthenticated(nextUser);

    return nextUser;
  }, [setSessionUser]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      status,
      user,
      refreshUser,
      updateAuthenticatedUser,
      logout,
    }),
    [isAuthenticated, status, user, refreshUser, updateAuthenticatedUser, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
}
